import React, { useState, useEffect, useRef, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import ClientBreadcrumb from "../../components/ClientBreadcrumb.jsx";
import { useAuth } from "../../hooks/useAuth.jsx";
import { useIsCompact } from "../../hooks/useIsCompact.js";
import { BASE_TAGS } from "../../lib/tagVocabulary.js";

// ── TagInput ──────────────────────────────────────────────────────────────────
// (unchanged from original)

function TagInput({ tags, onChange, suggestions, placeholder }) {
  const [inputVal, setInputVal] = useState("");
  const [open, setOpen] = useState(false);
  const inputRef = useRef();
  const wrapRef  = useRef();

  const filtered = suggestions.filter(s =>
    !tags.includes(s) &&
    (inputVal === "" || s.toLowerCase().includes(inputVal.toLowerCase()))
  ).slice(0, 10);

  function addTag(raw) {
    const tag = raw.trim().replace(/,+$/, "");
    if (!tag || tags.includes(tag)) { setInputVal(""); return; }
    onChange([...tags, tag]);
    setInputVal("");
  }
  function removeTag(tag) { onChange(tags.filter(t => t !== tag)); }
  function onKeyDown(e) {
    if (e.key === "Enter" || e.key === ",") { e.preventDefault(); if (inputVal.trim()) addTag(inputVal); }
    else if (e.key === "Backspace" && !inputVal && tags.length > 0) removeTag(tags[tags.length - 1]);
    else if (e.key === "Escape") setOpen(false);
  }

  useEffect(() => {
    function h(e) { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); }
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  return (
    <div ref={wrapRef} style={{ position: "relative" }}>
      <div style={ts.wrap} onClick={() => { inputRef.current?.focus(); setOpen(true); }}>
        {tags.map(tag => (
          <span key={tag} style={ts.chip}>
            {tag}
            <button style={ts.chipX} onClick={e => { e.stopPropagation(); removeTag(tag); }}>×</button>
          </span>
        ))}
        <input ref={inputRef} value={inputVal}
          onChange={e => { setInputVal(e.target.value); setOpen(true); }}
          onKeyDown={onKeyDown} onFocus={() => setOpen(true)}
          placeholder={tags.length === 0 ? (placeholder || "Add tags…") : ""}
          style={ts.input} />
      </div>
      {open && filtered.length > 0 && (
        <div style={ts.dropdown}>
          {inputVal === "" && <p style={ts.dropHint}>Suggested tags — click to add</p>}
          {filtered.map(s => (
            <button key={s} style={ts.option} onMouseDown={e => { e.preventDefault(); addTag(s); }}>{s}</button>
          ))}
        </div>
      )}
      <p style={ts.hint}>Enter or comma to add · Backspace to remove last</p>
    </div>
  );
}

const ts = {
  wrap: { minHeight:40, border:"1px solid var(--color-border)", borderRadius:"var(--radius-sm)", background:"var(--color-bg-base)", padding:"var(--space-1) var(--space-2)", display:"flex", flexWrap:"wrap", gap:"var(--space-1)", alignItems:"center", cursor:"text" },
  chip: { display:"inline-flex", alignItems:"center", gap:3, background:"var(--color-accent-subtle)", color:"var(--color-accent)", borderRadius:"var(--radius-full)", fontSize:"var(--text-xs)", fontWeight:"var(--weight-medium)", padding:"2px 8px", whiteSpace:"nowrap" },
  chipX: { background:"none", border:"none", color:"var(--color-accent)", cursor:"pointer", padding:0, fontSize:14, lineHeight:1, display:"flex", alignItems:"center", opacity:0.7 },
  input: { border:"none", outline:"none", background:"transparent", fontSize:"var(--text-sm)", color:"var(--color-text-primary)", minWidth:80, flex:1, padding:"2px 0" },
  dropdown: { position:"absolute", top:"calc(100% + 4px)", left:0, right:0, background:"var(--color-surface)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", boxShadow:"0 4px 12px rgba(0,0,0,.1)", zIndex:100, maxHeight:220, overflowY:"auto" },
  dropHint: { fontSize:10, color:"var(--color-text-tertiary)", padding:"6px 10px 2px", fontWeight:"var(--weight-medium)", textTransform:"uppercase", letterSpacing:"0.05em" },
  option: { display:"block", width:"100%", textAlign:"left", background:"none", border:"none", padding:"7px 10px", fontSize:"var(--text-sm)", color:"var(--color-text-primary)", cursor:"pointer" },
  hint: { fontSize:11, color:"var(--color-text-tertiary)", marginTop:"var(--space-1)", lineHeight:1.4 },
  suggestChip: { display:"inline-flex", alignItems:"center", gap:3, border:"1px dashed var(--color-accent)", color:"var(--color-accent)", borderRadius:"var(--radius-full)", fontSize:"var(--text-xs)", padding:"1px 8px", whiteSpace:"nowrap" },
  suggestAdd:  { background:"none", border:"none", color:"var(--color-accent)", cursor:"pointer", padding:0, fontSize:"var(--text-xs)" },
  suggestAll:  { background:"none", border:"none", color:"var(--color-text-secondary)", cursor:"pointer", padding:"1px 4px", fontSize:"var(--text-xs)", textDecoration:"underline" },
  select: { fontSize:"var(--text-xs)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-sm)", background:"var(--color-bg-base)", color:"var(--color-text-secondary)", padding:"2px 6px" },
};

// ── People in a photo (v3.5, Oct 26) ──────────────────────────────────────────
// Picking a person adds two tags, their first name and their relationship
// (Stephanie, mom). The server derives those tags from the pick, so the tag box
// shows only the photo's other tags.

export function personLabel(p) {
  return p.relationship ? `${p.first_name || p.display_name} · ${p.relationship}` : (p.first_name || p.display_name);
}

/** The tags that came from the people picked for a photo. */
export function personTagsOf(personIds, people) {
  const byId = new Map(people.map(p => [p.id, p]));
  return new Set((personIds || []).flatMap(id => byId.get(id)?.tags || []));
}

export function PeoplePicker({ personIds, people, onChange, placeholder = "+ Add a person…" }) {
  const byId = new Map(people.map(p => [p.id, p]));
  const linked = (personIds || []).filter(id => byId.has(id));
  const rest = people.filter(p => !linked.includes(p.id));
  if (!people.length) {
    return <p style={ts.hint}>Add people on the People page to mark who's in a photo.</p>;
  }
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-1)", alignItems: "center" }}>
      {linked.map(id => (
        <span key={id} style={ts.chip}>
          {personLabel(byId.get(id))}
          <button style={ts.chipX} aria-label={`Remove ${personLabel(byId.get(id))}`}
            onClick={() => onChange(linked.filter(x => x !== id))}>×</button>
        </span>
      ))}
      {rest.length > 0 && (
        <select aria-label="Add a person" value="" style={ts.select}
          onChange={e => { if (e.target.value) onChange([...linked, e.target.value]); }}>
          <option value="">{placeholder}</option>
          {rest.map(p => <option key={p.id} value={p.id}>{personLabel(p)}</option>)}
        </select>
      )}
    </div>
  );
}

// ── Suggested tags (PE v3.5.1, Oct 27) ─────────────────────────────────────────
// Suggestions wait on the photo until the coordinator adds or rejects each one;
// nothing is applied by itself. Wording is a first draft for David.

async function decideSuggestions(mediaId, accept, reject) {
  const res = await fetch(`/api/media/${mediaId}/suggestions`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ accept, reject }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || "Couldn't save that.");
  return data;
}

export function SuggestedTags({ suggestions, onAccept, onReject, onAcceptAll, busy }) {
  if (!suggestions || suggestions.length === 0) return null;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--space-1)", alignItems: "center" }}>
      {suggestions.map(sg => (
        <span key={sg.tag} style={ts.suggestChip}
          title={sg.because?.length ? `Seen in the photo: ${sg.because.join(", ")}` : undefined}>
          <button style={ts.suggestAdd} disabled={busy} onClick={() => onAccept(sg.tag)}
            aria-label={`Add ${sg.tag}`}>+ {sg.tag}</button>
          <button style={ts.chipX} disabled={busy} onClick={() => onReject(sg.tag)}
            aria-label={`Reject ${sg.tag}`}>×</button>
        </span>
      ))}
      {suggestions.length > 1 && (
        <button style={ts.suggestAll} disabled={busy} onClick={onAcceptAll}>Add all</button>
      )}
    </div>
  );
}

// ── UploadZone ────────────────────────────────────────────────────────────────

function UploadZone({ clientId, onUploaded }) {
  const [dragging,  setDragging]  = useState(false);
  const [uploading, setUploading] = useState(false);
  const [results,   setResults]   = useState([]);
  const inputRef = useRef();

  async function uploadFiles(files) {
    setUploading(true);
    const out = [];
    for (const file of files) {
      const form = new FormData();
      form.append("file", file);
      try {
        const res  = await fetch(`/api/clients/${clientId}/media`, { method:"POST", body:form });
        const data = await res.json();
        out.push({ name:file.name, ok:res.ok, error:data.error });
      } catch { out.push({ name:file.name, ok:false }); }
    }
    setResults(out);
    setUploading(false);
    onUploaded();
    setTimeout(() => setResults([]), 4000);
  }

  return (
    <div style={{ ...s.dropZone, ...(dragging ? s.dropZoneActive : {}) }}
      onDragOver={e => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={e => { e.preventDefault(); setDragging(false); uploadFiles(Array.from(e.dataTransfer.files)); }}
      onClick={() => !uploading && inputRef.current.click()}>
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple
        style={{ display:"none" }} onChange={e => uploadFiles(Array.from(e.target.files))} />
      {uploading ? <p style={s.dropText}>Uploading…</p> : (
        <>
          <span style={{ fontSize:32 }}>🖼</span>
          <p style={s.dropText}>Drop images here or click to browse</p>
          <p style={s.dropHint}>JPEG, PNG, WebP, GIF · max 10 MB · multi-select OK</p>
        </>
      )}
      {results.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:"var(--space-1)", fontSize:"var(--text-xs)", marginTop:"var(--space-2)" }}>
          {results.map((r,i) => (
            <span key={i} style={{ color:r.ok?"var(--color-status-live)":"var(--color-critical)" }}>
              {r.ok?"✓":"✗"} {r.name}{r.error?`: ${r.error}`:""}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Approving contributors' photos (Oct 25) ─────────────────────────────────
// A contributor's photo arrives unapproved: it's in the library, but nothing
// can use it — streams, pickers, the hero — until a coordinator approves it.
// The server reports which need approval (needs_approval) and who added them.
export async function approvePhotos(clientId, ids) {
  const res = await fetch(`/api/clients/${clientId}/media/approve`, {
    method: "POST", headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ media_ids: ids }),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Couldn't approve (${res.status})`);
  return data.approved || ids;
}

// ── ImageTile ─────────────────────────────────────────────────────────────────
// isSelected renders from the selection Set — fixes border bug by construction.
// Per-image X for quick single delete (independent of selection).

function ImageTile({ image, isSelected, onTileClick, onTileMouseEnter, onQuickDelete, isDragSelecting }) {
  const [hovered, setHovered] = useState(false);
  const highlight = isDragSelecting ? isSelected : isSelected;

  return (
    <div
      style={{ ...s.tile, ...(highlight ? s.tileSelected : {}), position:"relative" }}
      onClick={onTileClick}
      onMouseEnter={() => { setHovered(true); onTileMouseEnter(); }}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Per-image X delete button */}
      {hovered && (
        <button
          style={s.tileX}
          onClick={e => { e.stopPropagation(); onQuickDelete(image); }}
          title="Delete this image"
          aria-label="Delete image"
        >×</button>
      )}

      <div style={s.thumbWrap}>
        <img src={image.thumb_url} alt={image.alt_text || ""} style={s.thumbImg} />
        {(image.usage || []).length > 0 && (
          <div style={s.usageBadge} title={(image.usage||[]).map(u=>u.label).join(", ")}>
            {image.usage.length}
          </div>
        )}
        {image.needs_approval && (
          <div style={s.heldBadge} title={`Added by ${image.added_by || "a contributor"} — needs your approval before it can be used`}>
            Not approved
          </div>
        )}
      </div>

      <p style={s.tileName} title={image.filename}>{image.filename}</p>
      {image.alt_text && <p style={s.tileAlt} title={image.alt_text}>{image.alt_text}</p>}
      {(image.tags||[]).length > 0 && (
        <div style={s.tagRow}>
          {image.tags.slice(0,3).map(t => <span key={t} style={s.tagChipSmall}>{t}</span>)}
          {image.tags.length > 3 && <span style={s.tagChipSmall}>+{image.tags.length-3}</span>}
        </div>
      )}
    </div>
  );
}

// ── DetailDrawer (single-image panel) ─────────────────────────────────────────

function DetailDrawer({ clientId, image, onClose, onUpdated, onDeleted, tagSuggestions, people = [] }) {
  const [releasing, setReleasing] = useState(false);
  const [releaseErr, setReleaseErr] = useState(null);
  async function release() {
    setReleasing(true); setReleaseErr(null);
    try { await approvePhotos(clientId, [image.id]); onUpdated({ id: image.id, needs_approval: false }); }
    catch (e) { setReleaseErr(e.message); }
    finally { setReleasing(false); }
  }
  const [altText, setAltText] = useState(image.alt_text || "");
  const [tags,    setTags]    = useState(image.tags || []);
  const [personIds, setPersonIds] = useState(image.person_ids || []);
  const [saving,  setSaving]  = useState(false);
  const [deleting,setDeleting]= useState(false);
  const saveTagsRef = useRef();

  // The tag box shows the photo's other tags; its people's tags come from the
  // picks (the server re-derives them either way).
  const fromPeople = personTagsOf(personIds, people);
  const freeTags = tags.filter(t => !fromPeople.has(t));

  useEffect(() => {
    setAltText(image.alt_text || ""); setTags(image.tags || []); setPersonIds(image.person_ids || []);
  }, [image.id]);

  useEffect(() => {
    clearTimeout(saveTagsRef.current);
    saveTagsRef.current = setTimeout(async () => {
      const res  = await fetch(`/api/media/${image.id}`, { method:"PATCH", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ tags: freeTags, person_ids: personIds }) });
      const data = await res.json();
      if (res.ok) onUpdated(data);
    }, 400);
    return () => clearTimeout(saveTagsRef.current);
  }, [tags, personIds]);

  const [deciding, setDeciding] = useState(false);
  const [suggestErr, setSuggestErr] = useState(null);
  async function decide(accept, reject) {
    setDeciding(true); setSuggestErr(null);
    try {
      const data = await decideSuggestions(image.id, accept, reject);
      setTags(data.tags || []);
      onUpdated(data);
    } catch (e) { setSuggestErr(e.message); }
    finally { setDeciding(false); }
  }

  async function saveAlt() {
    setSaving(true);
    const res = await fetch(`/api/media/${image.id}`, { method:"PATCH", headers:{"Content-Type":"application/json"}, body:JSON.stringify({ alt_text:altText||null }) });
    setSaving(false);
    onUpdated(await res.json());
  }

  async function deleteImage(force=false) {
    setDeleting(true);
    const res = await fetch(`/api/media/${image.id}${force?"?force=1":""}`, { method:"DELETE" });
    if (res.status === 204) { onDeleted(image.id); onClose(); }
    else if (res.status === 409) {
      const data = await res.json();
      if (window.confirm(`Used in ${data.usage_count} placement(s):\n${data.usage.map(u=>u.label).join("\n")}\n\nRemove and delete?`)) deleteImage(true);
    }
    setDeleting(false);
  }

  return (
    <div style={s.drawer}>
      <div style={s.drawerHeader}>
        <h3 style={s.drawerTitle}>Image details</h3>
        <button style={s.closeBtn} onClick={onClose}>✕</button>
      </div>
      <div style={s.drawerBody}>
        <img src={image.display_url} alt={image.alt_text||""} style={s.drawerPreview} />
        <div style={s.drawerField}><label style={s.fieldLabel}>Filename</label><p style={s.fieldValue}>{image.filename}</p></div>
        {image.dimensions && <div style={s.drawerField}><label style={s.fieldLabel}>Dimensions</label><p style={s.fieldValue}>{image.dimensions.width} × {image.dimensions.height}</p></div>}
        {(image.usage||[]).length > 0 && (
          <div style={s.drawerField}>
            <label style={s.fieldLabel}>Used in</label>
            <div style={{ display:"flex", flexDirection:"column", gap:3 }}>
              {image.usage.map((u,i) => <span key={i} style={s.usageItem}>{u.label}</span>)}
            </div>
          </div>
        )}
        <div style={s.drawerField}>
          <label style={s.fieldLabel}>People in this photo</label>
          <PeoplePicker personIds={personIds} people={people} onChange={setPersonIds} />
        </div>
        <div style={s.drawerField}>
          <label style={s.fieldLabel}>Tags</label>
          <TagInput tags={freeTags} onChange={setTags} suggestions={tagSuggestions} />
        </div>
        {(image.suggested_tags || []).length > 0 && (
          <div style={s.drawerField}>
            <label style={s.fieldLabel}>Suggested tags</label>
            <SuggestedTags suggestions={image.suggested_tags} busy={deciding}
              onAccept={t => decide([t], [])}
              onReject={t => decide([], [t])}
              onAcceptAll={() => decide(image.suggested_tags.map(x => x.tag), [])} />
            {suggestErr && <p style={{ margin: 0, color: "var(--color-critical)", fontSize: "var(--text-xs)" }}>{suggestErr}</p>}
          </div>
        )}
        {image.needs_approval && (
          <div style={s.heldNote}>
            <p style={{ margin: 0 }}>
              Added by {image.added_by || "a contributor"}. Not approved yet — it can't
              be used in streams, sections or anywhere else until you approve it.
            </p>
            <button style={s.saveBtn} onClick={release} disabled={releasing}>
              {releasing ? "Approving…" : "Approve"}
            </button>
            {releaseErr && <p style={{ margin: 0, color: "var(--color-critical)" }}>{releaseErr}</p>}
          </div>
        )}
        <div style={s.drawerField}>
          <label style={s.fieldLabel}>Alt text</label>
          <textarea style={s.fieldTextarea} value={altText} onChange={e => setAltText(e.target.value)} rows={3}
            placeholder="Describe this image for screen readers and PE image selection…" />
          <button style={s.saveBtn} onClick={saveAlt} disabled={saving}>{saving?"Saving…":"Save alt text"}</button>
        </div>
        <button style={s.deleteBtn} onClick={() => { if (window.confirm("Delete this image?")) deleteImage(false); }} disabled={deleting}>
          {deleting?"Deleting…":"Delete image"}
        </button>
      </div>
    </div>
  );
}

// ── BulkPanel (multi-image panel) ─────────────────────────────────────────────

function BulkPanel({ clientId, selectedIds, images, onDeleted, onUpdatedAll, onClose, tagSuggestions, people = [] }) {
  const heldIds = images.filter(img => selectedIds.has(img.id) && img.needs_approval).map(img => img.id);
  const [releasing, setReleasing] = useState(false);
  const [releaseErr, setReleaseErr] = useState(null);
  async function handleRelease() {
    setReleasing(true); setReleaseErr(null);
    try {
      await approvePhotos(clientId, heldIds);
      onUpdatedAll(heldIds.map(id => ({ id, needs_approval: false })));
    } catch (e) { setReleaseErr(e.message); }
    finally { setReleasing(false); }
  }
  const [bulkTags,  setBulkTags]  = useState([]);
  const [bulkPeople, setBulkPeople] = useState([]);
  const [tagging,   setTagging]   = useState(false);
  const [deleting,  setDeleting]  = useState(false);
  const count = selectedIds.size;

  async function handleBulkDelete() {
    if (!window.confirm(`Delete ${count} image${count!==1?"s":""}? This cannot be undone.`)) return;
    setDeleting(true);
    const ids = [...selectedIds];
    for (const id of ids) {
      await fetch(`/api/media/${id}`, { method:"DELETE" }).catch(()=>{});
    }
    setDeleting(false);
    onDeleted(ids);
    onClose();
  }

  async function handleBulkTag() {
    if (!bulkTags.length && !bulkPeople.length) return;
    setTagging(true);
    const updated = [];
    for (const img of images.filter(img => selectedIds.has(img.id))) {
      const body = {};
      if (bulkTags.length) body.tags = [...new Set([...(img.tags||[]), ...bulkTags])];
      if (bulkPeople.length) body.person_ids = [...new Set([...(img.person_ids||[]), ...bulkPeople])];
      const res  = await fetch(`/api/media/${img.id}`, { method:"PATCH", headers:{"Content-Type":"application/json"}, body:JSON.stringify(body) });
      if (res.ok) updated.push(await res.json());
    }
    setTagging(false);
    setBulkTags([]);
    setBulkPeople([]);
    onUpdatedAll(updated);
  }
  const nothingToAdd = bulkTags.length === 0 && bulkPeople.length === 0;

  const withSuggestions = images.filter(img => selectedIds.has(img.id) && (img.suggested_tags || []).length > 0);
  const [accepting, setAccepting] = useState(false);
  async function handleAcceptAll() {
    setAccepting(true);
    const updated = [];
    for (const img of withSuggestions) {
      try { updated.push(await decideSuggestions(img.id, img.suggested_tags.map(x => x.tag), [])); }
      catch { /* a photo that failed keeps its suggestions */ }
    }
    setAccepting(false);
    onUpdatedAll(updated);
  }

  return (
    <div style={s.drawer}>
      <div style={s.drawerHeader}>
        <h3 style={s.drawerTitle}>{count} image{count!==1?"s":""} selected</h3>
        <button style={s.closeBtn} onClick={onClose}>✕</button>
      </div>
      <div style={s.drawerBody}>
        <p style={{ fontSize:"var(--text-sm)", color:"var(--color-text-secondary)" }}>
          Actions apply to all {count} selected images. Click an image to select just that one.
        </p>

        {heldIds.length > 0 && (
          <div style={s.heldNote}>
            <p style={{ margin: 0 }}>
              {heldIds.length === 1 ? "One of these was" : `${heldIds.length} of these were`} added
              by a contributor and {heldIds.length === 1 ? "isn't" : "aren't"} approved yet.
            </p>
            <button style={s.saveBtn} onClick={handleRelease} disabled={releasing}>
              {releasing ? "Approving…" : `Approve ${heldIds.length}`}
            </button>
            {releaseErr && <p style={{ margin: 0, color: "var(--color-critical)" }}>{releaseErr}</p>}
          </div>
        )}

        {withSuggestions.length > 0 && (
          <div style={s.heldNote}>
            <p style={{ margin: 0 }}>
              {withSuggestions.length === 1 ? "One of these has" : `${withSuggestions.length} of these have`} suggested tags.
            </p>
            <button style={s.saveBtn} onClick={handleAcceptAll} disabled={accepting}>
              {accepting ? "Adding…" : "Add all suggested tags"}
            </button>
          </div>
        )}

        {/* Bulk people and tags */}
        <div style={s.drawerField}>
          <label style={s.fieldLabel}>Add people to all selected</label>
          <PeoplePicker personIds={bulkPeople} people={people} onChange={setBulkPeople} />
        </div>
        <div style={s.drawerField}>
          <label style={s.fieldLabel}>Add tags to all selected</label>
          <TagInput tags={bulkTags} onChange={setBulkTags} suggestions={tagSuggestions}
            placeholder="Tags to add to all…" />
          <button style={{ ...s.saveBtn, ...(nothingToAdd||tagging ? {opacity:0.5,cursor:"not-allowed"} : {}) }}
            onClick={handleBulkTag} disabled={nothingToAdd||tagging}>
            {tagging ? "Applying…" : `Apply to ${count} image${count!==1?"s":""}`}
          </button>
        </div>

        {/* Bulk delete */}
        <div style={{ marginTop:"auto" }}>
          <button style={s.deleteBtn} onClick={handleBulkDelete} disabled={deleting}>
            {deleting ? "Deleting…" : `Delete ${count} image${count!==1?"s":""}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main ──────────────────────────────────────────────────────────────────────

export default function MediaLibrary() {
  const { clientId }   = useParams();
  const { user }       = useAuth();
  const compact        = useIsCompact();
  const [images, setImages]         = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState("");
  const [tagFilter, setTagFilter]   = useState(new Set());
  const [unplacedOnly, setUnplaced] = useState(false);
  const [suggestedOnly, setSuggestedOnly] = useState(false);
  const [tagging, setTagging] = useState(null);      // {enabled, waiting, batch_size}
  const [suggesting, setSuggesting] = useState(null); // progress text while running
  const [suggestMsg, setSuggestMsg] = useState(null);
  const [people, setPeople] = useState([]);

  // ── Selection state — a Set of selected image IDs ────────────────────────
  // All selection visuals render from this Set.
  // Border bug is fixed by construction: when the Set changes, stale borders
  // are not rendered because the image is no longer in the Set.
  const [selectedIds, setSelectedIds] = useState(new Set());
  const lastClickedIdx  = useRef(-1);  // for Shift-click range

  // Drag/marquee selection state
  const [dragIds, setDragIds]     = useState(null);  // null = not dragging; Set = in progress
  const isDragging                = useRef(false);
  const dragAccumulator           = useRef(new Set());

  const allTags      = [...new Set(images.flatMap(img => img.tags||[]))].sort();
  // The tag box offers the library's own tags and the shared list (base@1);
  // people are picked separately, so their tags aren't offered here.
  const peopleTags     = new Set(people.flatMap(p => p.tags || []));
  const tagSuggestions = [...new Set([...allTags.filter(t => !peopleTags.has(t)), ...BASE_TAGS])].sort();

  const fetchImages = useCallback(async () => {
    setLoading(true);
    try {
      const res  = await fetch(`/api/clients/${clientId}/media`);
      const data = await res.json();
      setImages(Array.isArray(data) ? data : []);
    } catch { /**/ }
    setLoading(false);
  }, [clientId]);

  useEffect(() => { fetchImages(); }, [fetchImages]);

  useEffect(() => {
    fetch(`/api/clients/${clientId}/people`).then(r=>r.json()).then(data => {
      if (Array.isArray(data)) setPeople(data);
    }).catch(()=>{});
  }, [clientId]);

  const loadTagging = useCallback(() => {
    fetch(`/api/clients/${clientId}/media/suggest-tags`).then(r => (r.ok ? r.json() : null))
      .then(d => setTagging(d)).catch(() => {});
  }, [clientId]);
  useEffect(() => { loadTagging(); }, [loadTagging]);

  // Suggest tags for the selected photos, or for every approved photo that has
  // never had suggestions — in batches, so no single request runs long.
  async function runSuggest() {
    const size = tagging?.batch_size || 25;
    const ids = [...selectedIds];
    const batches = ids.length ? Array.from({ length: Math.ceil(ids.length / size) }, (_, i) => ids.slice(i * size, (i + 1) * size)) : null;
    const total = ids.length || tagging?.waiting || 0;
    let done = 0, found = 0, error = null;
    setSuggestMsg(null);
    for (let round = 0; round < (batches ? batches.length : Math.ceil(total / size) + 1); round++) {
      setSuggesting(`Suggesting tags… ${Math.min(done, total)} of ${total}`);
      const res = await fetch(`/api/clients/${clientId}/media/suggest-tags`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(batches ? { media_ids: batches[round] } : {}),
      }).catch(() => null);
      const data = res ? await res.json().catch(() => ({})) : {};
      if (!res || !res.ok) { error = data.error || "Couldn't get suggestions right now."; break; }
      done += (data.photos || 0) + (data.unlabelled || 0);
      found += data.suggested || 0;
      if (!batches && !data.remaining) break;
      if (!batches && !(data.photos || data.unlabelled)) break;   // nothing moved: stop
    }
    setSuggesting(null);
    setSuggestMsg(error || (found ? `${found} tag${found !== 1 ? "s" : ""} suggested. Open a photo to add or reject them.` : "No new tags to suggest."));
    if (found) setSuggestedOnly(true);
    fetchImages();
    loadTagging();
  }

  // End drag on mouseup anywhere in the document
  useEffect(() => {
    function onMouseUp() {
      if (!isDragging.current) return;
      isDragging.current = false;
      setSelectedIds(new Set(dragAccumulator.current));
      setDragIds(null);
      dragAccumulator.current = new Set();
    }
    document.addEventListener("mouseup", onMouseUp);
    return () => document.removeEventListener("mouseup", onMouseUp);
  }, []);

  const filtered = images.filter(img => {
    if (search) {
      const q = search.toLowerCase();
      if (!img.filename.toLowerCase().includes(q) &&
          !(img.alt_text||"").toLowerCase().includes(q) &&
          !(img.tags||[]).some(t=>t.toLowerCase().includes(q))) return false;
    }
    if (tagFilter.size > 0) {
      const it = img.tags||[];
      if (![...tagFilter].every(t=>it.includes(t))) return false;
    }
    if (unplacedOnly && (img.usage||[]).length > 0) return false;
    if (suggestedOnly && !(img.suggested_tags||[]).length) return false;
    return true;
  });

  // ── Selection operations ──────────────────────────────────────────────────

  function handleTileClick(image, e, filteredIdx) {
    e.stopPropagation();           // prevent bubbling to main clear-selection handler
    if (isDragging.current) return; // mouseup will commit drag selection

    if (e.shiftKey && lastClickedIdx.current >= 0) {
      // Range select — contiguous from last click to this click in filtered list
      const lo = Math.min(lastClickedIdx.current, filteredIdx);
      const hi = Math.max(lastClickedIdx.current, filteredIdx);
      const rangeIds = filtered.slice(lo, hi+1).map(img=>img.id);
      setSelectedIds(prev => new Set([...prev, ...rangeIds]));
    } else if (e.metaKey || e.ctrlKey) {
      // Toggle single
      setSelectedIds(prev => {
        const next = new Set(prev);
        next.has(image.id) ? next.delete(image.id) : next.add(image.id);
        return next;
      });
      lastClickedIdx.current = filteredIdx;
    } else {
      // Single select — replaces selection
      setSelectedIds(new Set([image.id]));
      lastClickedIdx.current = filteredIdx;
    }
  }

  function startDrag(imageId) {
    isDragging.current = true;
    dragAccumulator.current = new Set([imageId]);
    setDragIds(new Set([imageId]));
  }

  function continueDrag(imageId) {
    if (!isDragging.current) return;
    dragAccumulator.current.add(imageId);
    setDragIds(new Set(dragAccumulator.current));
  }

  // ── Update / delete callbacks ─────────────────────────────────────────────

  const heldCount = images.filter(img => img.needs_approval).length;

  function onImageUpdated(updated) {
    setImages(prev => prev.map(img => img.id===updated.id ? {...img,...updated} : img));
  }
  function onImagesUpdatedAll(updates) {
    const map = Object.fromEntries(updates.map(u=>[u.id,u]));
    setImages(prev => prev.map(img => map[img.id] ? {...img,...map[img.id]} : img));
  }
  function onImageDeleted(id) {
    setImages(prev => prev.filter(img => img.id!==id));
    setSelectedIds(prev => { const n=new Set(prev); n.delete(id); return n; });
  }
  function onImagesDeleted(ids) {
    const idSet = new Set(ids);
    setImages(prev => prev.filter(img => !idSet.has(img.id)));
    setSelectedIds(new Set());
  }

  // Per-image X quick delete
  async function onQuickDelete(image) {
    if (!window.confirm(`Delete "${image.filename}"?`)) return;
    const res = await fetch(`/api/media/${image.id}`, { method:"DELETE" });
    if (res.status === 204) onImageDeleted(image.id);
    else if (res.status === 409) {
      const data = await res.json();
      if (window.confirm(`Used in ${data.usage_count} placement(s). Remove and delete?`)) {
        await fetch(`/api/media/${image.id}?force=1`, { method:"DELETE" });
        onImageDeleted(image.id);
      }
    }
  }

  // ── Derive panel mode ─────────────────────────────────────────────────────

  const selectionCount  = selectedIds.size;
  const singleImage     = selectionCount === 1
    ? images.find(img => img.id === [...selectedIds][0]) || null
    : null;

  // The effective selection for visuals (drag preview overrides during drag)
  const effectiveIds = dragIds ?? selectedIds;

  return (
    <div style={s.page}>
      <div style={s.body}>
        <div style={s.main} onClick={() => setSelectedIds(new Set())}>

          {/* Nav */}
          <div style={s.topBar}>
            <ClientBreadcrumb clientName="Media library" role={user?.role} />
          </div>
          <h1 style={s.title}>Media library</h1>

          <UploadZone clientId={clientId} onUploaded={fetchImages} />

          {/* Contributors' photos waiting for approval (Oct 25) */}
          {heldCount > 0 && (
            <div style={s.heldBanner}>
              <span>
                {heldCount === 1 ? "1 photo" : `${heldCount} photos`} from contributors{" "}
                {heldCount === 1 ? "needs" : "need"} your approval before {heldCount === 1 ? "it" : "they"} can be used.
              </span>
              <button style={s.filterChip}
                onClick={e => { e.stopPropagation(); setSelectedIds(new Set(images.filter(i => i.needs_approval).map(i => i.id))); }}>
                Select {heldCount === 1 ? "it" : "them"}
              </button>
            </div>
          )}

          {/* Filter bar */}
          <div style={s.filterBar}>
            <input style={s.searchInput} placeholder="Search filename, alt text, or tags…"
              value={search} onChange={e => setSearch(e.target.value)} />
            <button style={{ ...s.filterChip, ...(unplacedOnly?s.filterChipActive:{}) }}
              onClick={() => setUnplaced(!unplacedOnly)}>Unplaced</button>
            {images.some(i => (i.suggested_tags||[]).length) && (
              <button style={{ ...s.filterChip, ...(suggestedOnly?s.filterChipActive:{}) }}
                onClick={() => setSuggestedOnly(!suggestedOnly)}>Has suggestions</button>
            )}
            {tagFilter.size > 0 && (
              <button style={{ ...s.filterChip, color:"var(--color-critical)", borderColor:"var(--color-critical)" }}
                onClick={() => setTagFilter(new Set())}>✕ Clear</button>
            )}
            {allTags.map(tag => (
              <button key={tag}
                style={{ ...s.filterChip, ...(tagFilter.has(tag)?s.filterChipActive:{}) }}
                onClick={() => setTagFilter(prev => { const n=new Set(prev); n.has(tag)?n.delete(tag):n.add(tag); return n; })}>
                {tag}
              </button>
            ))}
          </div>

          {tagging?.enabled && (
            <div style={{ display:"flex", gap:"var(--space-3)", alignItems:"center", flexWrap:"wrap" }}>
              <button style={s.saveBtn} disabled={!!suggesting || (selectionCount === 0 && !tagging.waiting)}
                onClick={e => { e.stopPropagation(); runSuggest(); }}>
                {suggesting || (selectionCount > 0
                  ? `Suggest tags for ${selectionCount} selected`
                  : tagging.waiting ? `Suggest tags for ${tagging.waiting} photo${tagging.waiting !== 1 ? "s" : ""}`
                  : "Suggest tags")}
              </button>
              {suggestMsg && <span style={{ fontSize:"var(--text-xs)", color:"var(--color-text-secondary)" }}>{suggestMsg}</span>}
            </div>
          )}

          {/* Count + selection hint */}
          <div style={{ display:"flex", gap:"var(--space-4)", alignItems:"center" }}>
            <p style={s.count}>{loading?"Loading…":`${filtered.length} of ${images.length} images`}</p>
            {selectionCount > 0 && (
              <p style={{ ...s.count, color:"var(--color-accent)" }}>
                {selectionCount} selected
                <button style={{ background:"none", border:"none", fontSize:11, color:"var(--color-text-tertiary)", cursor:"pointer", marginLeft:6 }}
                  onClick={() => setSelectedIds(new Set())}>Clear</button>
              </p>
            )}
            {!loading && filtered.length > 1 && (
              <button style={{ background:"none", border:"none", fontSize:11, color:"var(--color-accent)", cursor:"pointer", padding:0 }}
                onClick={e => { e.stopPropagation(); setSelectedIds(new Set(filtered.map(i => i.id))); }}>
                Select all {filtered.length}
              </button>
            )}
            {selectionCount === 0 && !loading && filtered.length > 0 && (
              <p style={{ ...s.count, fontStyle:"italic" }}>Click to select · Cmd/Ctrl-click to add · Shift-click for a range</p>
            )}
          </div>

          {/* Grid — drag starts here */}
          <div style={s.grid}
            onMouseDown={e => {
              // Only start drag on grid background click (not on tile controls)
              if (e.target === e.currentTarget) {
                setSelectedIds(new Set());
                lastClickedIdx.current = -1;
              }
            }}>
            {!loading && filtered.length === 0 && (
              <p style={s.empty}>No images yet. Drop some above to get started.</p>
            )}
            {filtered.map((img, idx) => (
              <ImageTile
                key={img.id}
                image={img}
                isSelected={effectiveIds.has(img.id)}
                isDragSelecting={!!dragIds}
                onTileClick={e => handleTileClick(img, e, idx)}
                onTileMouseEnter={() => {
                  if (isDragging.current) continueDrag(img.id);
                }}
                onQuickDelete={onQuickDelete}
              />
            ))}
          </div>
        </div>

        {/* Right panel — unified: idle / single detail / bulk actions.
            On compact (phone), the IDLE panel is suppressed entirely — a blank
            "select an image" column would just eat the narrow viewport and hide
            the library. Real drawers (single/bulk) render as a full-width
            overlay on compact instead of a cramped 320px side column. */}
        {selectionCount === 0 && !compact && (
          <div style={{ ...s.drawer, alignItems:"center", justifyContent:"center" }}>
            <p style={{ fontSize:"var(--text-sm)", color:"var(--color-text-tertiary)", textAlign:"center", padding:"var(--space-6)" }}>
              Select an image to view details,<br />or select multiple for bulk actions.
            </p>
          </div>
        )}
        {selectionCount === 1 && singleImage && (
          <div style={compact ? s.drawerOverlay : undefined}>
          <DetailDrawer
            clientId={clientId}
            image={singleImage}
            onClose={() => setSelectedIds(new Set())}
            onUpdated={onImageUpdated}
            onDeleted={onImageDeleted}
            tagSuggestions={tagSuggestions}
            people={people}
          />
          </div>
        )}
        {selectionCount > 1 && (
          <div style={compact ? s.drawerOverlay : undefined}>
          <BulkPanel
            clientId={clientId}
            selectedIds={selectedIds}
            images={images}
            onDeleted={onImagesDeleted}
            onUpdatedAll={onImagesUpdatedAll}
            onClose={() => setSelectedIds(new Set())}
            tagSuggestions={tagSuggestions}
            people={people}
          />
          </div>
        )}
      </div>
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  page:   { minHeight:"100dvh", background:"var(--color-bg-subtle)" },
  body:   { display:"flex", height:"100dvh", overflow:"hidden" },
  main:   { flex:1, overflow:"auto", padding:"var(--space-6)", display:"flex", flexDirection:"column", gap:"var(--space-5)" },
  topBar: { display:"flex", alignItems:"center", gap:"var(--space-4)" },
  title:  { fontSize:"var(--text-2xl)", fontWeight:"var(--weight-bold)", color:"var(--color-text-primary)" },

  dropZone:       { background:"var(--color-surface)", border:"2px dashed var(--color-border)", borderRadius:"var(--radius-xl)", padding:"var(--space-8)", display:"flex", flexDirection:"column", alignItems:"center", gap:"var(--space-2)", cursor:"pointer", transition:"border-color 0.15s" },
  dropZoneActive: { borderColor:"var(--color-accent)", background:"var(--color-accent-subtle)" },
  dropText:       { fontSize:"var(--text-base)", fontWeight:"var(--weight-medium)", color:"var(--color-text-secondary)" },
  dropHint:       { fontSize:"var(--text-sm)", color:"var(--color-text-tertiary)" },

  filterBar:       { display:"flex", gap:"var(--space-2)", flexWrap:"wrap", alignItems:"center" },
  searchInput:     { fontSize:"var(--text-sm)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", padding:"var(--space-2) var(--space-3)", background:"var(--color-surface)", color:"var(--color-text-primary)", minWidth:220 },
  filterChip:      { fontSize:"var(--text-xs)", padding:"3px var(--space-3)", borderRadius:"var(--radius-full)", border:"1px solid var(--color-border)", background:"var(--color-bg-base)", color:"var(--color-text-secondary)", cursor:"pointer" },
  filterChipActive:{ background:"var(--color-accent)", color:"var(--color-text-on-accent)", borderColor:"var(--color-accent)" },
  count:           { fontSize:"var(--text-xs)", color:"var(--color-text-tertiary)" },

  grid:    { display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(160px, 1fr))", gap:"var(--space-4)", alignContent:"start", userSelect:"none" },
  empty:   { fontSize:"var(--text-sm)", color:"var(--color-text-tertiary)", gridColumn:"1/-1", textAlign:"center", padding:"var(--space-10)" },

  tile:         { background:"var(--color-surface)", border:"2px solid transparent", borderRadius:"var(--radius-lg)", overflow:"hidden", cursor:"pointer", display:"flex", flexDirection:"column" },
  tileSelected: { background:"var(--color-accent-subtle)" },
  tileX:        { position:"absolute", top:6, right:6, width:20, height:20, borderRadius:"var(--radius-full)", background:"rgba(0,0,0,0.55)", color:"#fff", border:"none", fontSize:14, lineHeight:1, cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", zIndex:10 },
  thumbWrap:    { width:"100%", height:160, display:"flex", alignItems:"center", justifyContent:"center", position:"relative" },
  thumbImg:     { display:"block", maxWidth:"100%", maxHeight:160, width:"auto", height:"auto", borderRadius:8 },
  heldBadge:    { position:"absolute", bottom:"var(--space-1)", left:"var(--space-1)", background:"rgba(0,0,0,0.65)", color:"#fff", borderRadius:"var(--radius-sm)", fontSize:10, padding:"2px 6px", fontWeight:"var(--weight-semibold)" },
  heldNote:     { display:"flex", flexDirection:"column", gap:"var(--space-2)", fontSize:"var(--text-sm)", color:"var(--color-text-secondary)", background:"var(--color-bg-subtle)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", padding:"var(--space-3)" },
  heldBanner:   { display:"flex", alignItems:"center", gap:"var(--space-3)", flexWrap:"wrap", fontSize:"var(--text-sm)", color:"var(--color-text-secondary)", background:"var(--color-bg-subtle)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", padding:"var(--space-2) var(--space-3)" },
  usageBadge:   { position:"absolute", top:"var(--space-1)", right:"var(--space-1)", background:"var(--color-accent)", color:"#fff", borderRadius:"var(--radius-full)", minWidth:20, height:20, fontSize:10, display:"flex", alignItems:"center", justifyContent:"center", fontWeight:"bold", padding:"0 4px" },
  tileName:     { fontSize:"var(--text-xs)", color:"var(--color-text-secondary)", padding:"var(--space-2) var(--space-2) var(--space-1)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  tileAlt:      { fontSize:10, color:"var(--color-text-tertiary)", padding:"0 var(--space-2)", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" },
  tagRow:       { display:"flex", gap:3, flexWrap:"wrap", padding:"var(--space-1) var(--space-2) var(--space-2)" },
  tagChipSmall: { fontSize:9, background:"var(--color-bg-subtle)", color:"var(--color-text-tertiary)", borderRadius:"var(--radius-full)", padding:"1px 5px" },

  drawer:       { width:320, flexShrink:0, background:"var(--color-surface)", borderLeft:"1px solid var(--color-border)", display:"flex", flexDirection:"column", overflow:"hidden" },
  drawerOverlay:{ position:"fixed", inset:0, zIndex:300, background:"var(--color-surface)", display:"flex", flexDirection:"column", overflow:"auto" },
  drawerHeader: { display:"flex", alignItems:"center", justifyContent:"space-between", padding:"var(--space-5)", borderBottom:"1px solid var(--color-border)" },
  drawerTitle:  { fontSize:"var(--text-base)", fontWeight:"var(--weight-semibold)", color:"var(--color-text-primary)" },
  closeBtn:     { background:"none", border:"none", color:"var(--color-text-tertiary)", cursor:"pointer", fontSize:"var(--text-lg)", padding:0 },
  drawerBody:   { flex:1, overflow:"auto", padding:"var(--space-5)", display:"flex", flexDirection:"column", gap:"var(--space-4)" },
  drawerPreview:{ width:"100%", borderRadius:"var(--radius-md)", objectFit:"contain", maxHeight:200 },
  drawerField:  { display:"flex", flexDirection:"column", gap:"var(--space-2)" },
  fieldLabel:   { fontSize:"var(--text-xs)", fontWeight:"var(--weight-semibold)", textTransform:"uppercase", letterSpacing:"0.06em", color:"var(--color-text-tertiary)" },
  fieldValue:   { fontSize:"var(--text-sm)", color:"var(--color-text-secondary)" },
  fieldTextarea:{ fontSize:"var(--text-sm)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-sm)", padding:"var(--space-2) var(--space-3)", background:"var(--color-bg-base)", color:"var(--color-text-primary)", width:"100%", resize:"vertical", fontFamily:"inherit" },
  usageItem:    { fontSize:"var(--text-xs)", color:"var(--color-text-secondary)", background:"var(--color-bg-subtle)", borderRadius:"var(--radius-sm)", padding:"2px var(--space-2)" },
  saveBtn:      { fontSize:"var(--text-sm)", color:"var(--color-text-secondary)", background:"var(--color-bg-subtle)", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", padding:"var(--space-2) var(--space-4)", cursor:"pointer", alignSelf:"flex-start" },
  deleteBtn:    { fontSize:"var(--text-sm)", color:"var(--color-critical)", background:"var(--color-critical-bg)", border:"1px solid var(--color-critical)", borderRadius:"var(--radius-md)", padding:"var(--space-2) var(--space-4)", cursor:"pointer", marginTop:"auto" },
  cancelBtn:    { fontSize:"var(--text-sm)", color:"var(--color-text-secondary)", background:"none", border:"1px solid var(--color-border)", borderRadius:"var(--radius-md)", padding:"var(--space-2) var(--space-4)", cursor:"pointer" },
};
